import { HTMLAttributes, MouseEvent, RefObject, useRef } from 'react';
import { Key } from '@react-types/shared';
import { focusVisibleClasses } from '@heroui/theme';
import { MdOutlineCancel } from 'react-icons/md';
import TmdxChip from '../Chips';
import TruncatedWithTooltip from '../TruncatedWithTooltip';
import { cn } from '../../cn';

type ChipItem = {
  key: Key;
  textValue: string;
};

type RemovableChipsProps = Pick<HTMLAttributes<HTMLUListElement>, 'aria-label' | 'aria-labelledby'> & {
  items: ChipItem[];
  disabledKeys: Set<Key>;
  triggerRef: RefObject<HTMLElement>;
  onRemove: (key: Key) => void;
};

export const RemovableChips = ({ items, disabledKeys, triggerRef, onRemove, ...listProps }: RemovableChipsProps) => {
  const listRef = useRef<HTMLUListElement>(null);

  const handleRemove = (event: MouseEvent<HTMLButtonElement>, key: Key) => {
    const removeButtons = Array.from(listRef.current?.querySelectorAll('button') ?? []);
    const index = removeButtons.indexOf(event.currentTarget);
    const nextFocusTarget = removeButtons[index + 1] ?? removeButtons[index - 1] ?? triggerRef.current;

    nextFocusTarget.focus();
    onRemove(key);
  };

  if (!items.length) return null;

  return (
    <ul ref={listRef} className="flex flex-wrap gap-2 mt-2 pointer-events-auto" {...listProps}>
      {items.map(({ key, textValue }) => {
        const removeButton = disabledKeys.has(key) ? null : (
          <button
            type="button"
            aria-label={`Remove ${textValue}`}
            className={cn(
              'flex items-center shrink-0 rounded-full cursor-pointer opacity-70 hover:opacity-100 focus-visible:opacity-100 [&>svg]:size-4',
              focusVisibleClasses,
            )}
            onClick={(event) => handleRemove(event, key)}
          >
            <MdOutlineCancel aria-hidden />
          </button>
        );

        return (
          <li key={key} className="flex min-w-0 max-w-full">
            <TmdxChip
              size="sm"
              color="tertiary"
              className={cn('min-w-0 max-w-full', removeButton && 'pl-2 pr-1.5')}
              endContent={removeButton}
            >
              <TruncatedWithTooltip value={textValue} />
            </TmdxChip>
          </li>
        );
      })}
    </ul>
  );
};
